import WalletBalanceAction from "./WalletBalanceAction";
import React, { useContext, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import AddIcon from "@material-ui/icons/Add";
import PeopleIcon from "@material-ui/icons/People";
import PersonOutlinedIcon from "@material-ui/icons/PersonOutlined";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import {
  dueKind,
  liveKind,
  matchesQuery,
  money,
  moneyBoth,
  readQuery,
  renewPriceOf,
  replaceQuery,
  subSlotText,
} from "./helpers";
import {
  ContactAvatar,
  DueBadge,
  EmptyHint,
  FilterChips,
  LoadingHint,
  NegocioPager,
  KpiTile,
  PageFrame,
  SearchField,
  tNegocio,
  useMobile,
  usePager,
} from "./shared";
import { AccountMark, AccountPeek, ProductMark } from "./AccountPeek";
import { AddCartItemDialog, CartConfigDialog, CartDetailDialog, CartRowActions } from "./CartDialog";
import { ServiceRowActions, SendPromptDialog } from "./CustomerServiceDialog";
import { useRecords } from "./Lists";
import { useStyles } from "./styles";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { openBinancePaymentFromNotify } from "./BinanceCheckoutDialog";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import WatoolxServicesView, { useWatoolxServices } from "./WatoolxServicesView";

export default function ServicesPage() {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const isMobile = useMobile();
  const { records, loading, reload } = useRecords("subscriptions");
  const { user } = useContext(AuthContext);
  // Planes WaToolX (empresas de la plataforma): solo el super admin, solo lectura, no son suscripciones.
  const watoolx = useWatoolxServices(!!user?.super);
  const customers = useRecords("customers");
  const payments = useRecords("payment-methods");
  const refresh = async () => {
    setNow(Date.now());
    await Promise.all([reload({ soft: true }), customers.reload({ soft: true })]);
  };
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("");
  const [party, setParty] = useState("");
  const [view, setView] = useState("cart");
  const [peekAccountId, setPeekAccountId] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [configPreset, setConfigPreset] = useState(null);
  const [cartId, setCartId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [pendingAdd, setPendingAdd] = useState(null);
  const [sendPrompt, setSendPrompt] = useState(null);
  const batchSinceRef = useRef(null);
  const [now, setNow] = useState(() => Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(id);
  }, []);

  React.useEffect(() => {
    const params = readQuery(location.search);
    if (params.contactId) setCartId(Number(params.contactId));
    if (params.accountId) setPeekAccountId(Number(params.accountId));
    if (params.add === "1") setPendingAdd(params.contactId ? Number(params.contactId) : true);
  }, [location.search]);

  React.useEffect(() => {
    if (!pendingAdd) return;
    if (customers.loading) return;
    if (pendingAdd === true) {
      setConfigPreset(null);
      setConfigOpen(true);
      setPendingAdd(null);
      return;
    }
    const customer = (customers.records || []).find((c) => Number(c.contactId) === Number(pendingAdd));
    openCart(pendingAdd);
    if (customer?.cartSaleType) setAddOpen(true);
    else {
      setConfigPreset(customer || { contactId: pendingAdd });
      setConfigOpen(true);
    }
    setPendingAdd(null);
  }, [pendingAdd, customers.loading, customers.records]);

  const customerByContact = useMemo(() => {
    const map = new Map();
    (customers.records || []).forEach((c) => map.set(Number(c.contactId), c));
    return map;
  }, [customers.records]);

  const groups = useMemo(() => {
    const byContact = new Map();
    const ensure = (contactId, seed) => {
      if (!byContact.has(contactId)) {
        const customer = customerByContact.get(contactId);
        byContact.set(contactId, {
          contactId,
          name: seed?.name || customer?.name || "—",
          number: seed?.number || customer?.number || "",
          customerType: customer?.customerType || "final",
          code: customer?.code || "",
          items: [],
          activeCount: 0,
          pendingCount: 0,
          expiredCount: 0,
          amount: 0,
        });
      }
      return byContact.get(contactId);
    };
    records.forEach((sub) => {
      // /negocio/subscriptions ya incluye sub.sale.status; no hace falta cargar ventas.
      const status = liveKind(sub);
      if (!status) return;
      const contactId = Number(sub.contactId || sub.contact?.id);
      if (!contactId) return;
      const group = ensure(contactId, sub.contact);
      const item = { ...sub, liveStatus: status, due: dueKind(sub) };
      group.items.push(item);
      group.amount += renewPriceOf(sub);
      if (status === "expired") group.expiredCount += 1;
      else if (status === "pending") group.pendingCount += 1;
      else group.activeCount += 1;
    });
    (customers.records || []).forEach((c) => {
      if (!c.cartSaleType) return;
      ensure(Number(c.contactId), c);
    });
    return Array.from(byContact.values())
      .map((g) => ({
        ...g,
        items: g.items.sort((a, b) => String(a.endDate || "").localeCompare(String(b.endDate || ""))),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [records, customerByContact, customers.records]);

  const rows = useMemo(
    () =>
      groups
        .map((g) => {
          if (party && g.customerType !== party) return null;
          const items = kind ? g.items.filter((item) => item.liveStatus === kind || item.due === kind) : g.items;
          if (kind && !items.length) return null;
          const productNames = items.map((item) => item.product?.name);
          if (!matchesQuery(g, query, [g.name, g.number, g.code, ...productNames])) return null;
          if (!kind) return g;
          return {
            ...g,
            items,
            activeCount: items.filter((item) => item.liveStatus === "active" || item.liveStatus === "soon" || item.liveStatus === "today").length,
            pendingCount: items.filter((item) => item.liveStatus === "pending").length,
            expiredCount: items.filter((item) => item.liveStatus === "expired").length,
            amount: items.reduce((n, item) => n + renewPriceOf(item), 0),
          };
        })
        .filter(Boolean),
    [groups, query, kind, party]
  );

  const flatItems = useMemo(() => rows.flatMap((g) => g.items.map((item) => ({ ...item, group: g }))), [rows]);
  const groupPager = usePager(rows);
  const listPager = usePager(flatItems);

  const kpis = useMemo(() => {
    const clients = groups.length;
    const live = groups.reduce((n, g) => n + g.items.length, 0);
    const expired = groups.reduce((n, g) => n + g.expiredCount, 0);
    const pending = groups.reduce((n, g) => n + g.pendingCount, 0);
    return { clients, live, expired, pending };
  }, [groups]);

  const finalCount = groups.filter((g) => g.customerType !== "reseller").length;
  const resellerCount = groups.filter((g) => g.customerType === "reseller").length;

  const openCart = (contactId) => {
    const next = contactId ? Number(contactId) : null;
    setCartId(next);
    const patch = { contactId: next };
    if (!next || Number(next) !== Number(cartId)) {
      setPeekAccountId(null);
      patch.accountId = null;
    }
    replaceQuery(history, location, patch);
  };
  const showAccount = (id) => {
    const next = id ? Number(id) : null;
    setPeekAccountId(next);
    replaceQuery(history, location, { accountId: next });
  };
  const cartGroup = rows.find((g) => Number(g.contactId) === Number(cartId)) || groups.find((g) => Number(g.contactId) === Number(cartId));
  const cartCustomer = customerByContact.get(Number(cartId)) || null;

  const notifyCart = async ({ kind, channel, contactId, sinceUnix, subscriptionIds }) => {
    try {
      const { data } = await api.post("/negocio/notify", {
        notify: true,
        cart: true,
        kind,
        channel,
        contactId,
        ...(sinceUnix != null ? { sinceUnix } : {}),
        ...(subscriptionIds?.length ? { subscriptionIds } : {}),
      });
      toast.success(tNegocio("pages.customers.sentOk"));
      openBinancePaymentFromNotify(data);
    } catch (e) {
      toastError(e);
    }
  };

  const openSendLast = (customer, subscriptionIds = []) => {
    if (!customer) return;
    if (batchSinceRef.current == null) batchSinceRef.current = { contactId: customer.contactId, ids: [] };
    const batch = batchSinceRef.current;
    batch.ids = [...new Set([...batch.ids, ...subscriptionIds])];
    setSendPrompt({ kind: "last", customer, cart: true, subscriptionIds: batch.ids });
  };

  const closeSendPrompt = () => {
    setSendPrompt(null);
    batchSinceRef.current = null;
  };

  const notifyCustomer = async ({ kind: notifyKind, channel, contactId, subscriptionId }) => {
    try {
      const { data } = await api.post("/negocio/notify", {
        notify: true,
        kind: notifyKind,
        channel,
        contactId,
        subscriptionId,
      });
      toast.success(tNegocio("pages.customers.sentOk"));
      openBinancePaymentFromNotify(data);
    } catch (e) {
      toastError(e);
    }
  };

  const renderServiceRow = (s, extra) => (
    <TableRow
      key={s.id}
      hover
      className={classes.clickableRow}
      onClick={() => {
        if (extra?.onRowClick) extra.onRowClick();
        else if (s.accountId) showAccount(s.accountId);
      }}
    >
      {extra?.contactCell ? <TableCell>{extra.contactCell}</TableCell> : null}
      <TableCell>
        <span className={classes.optionRow}>
          <ProductMark product={s.product} size={24} circle />
          {s.product?.name}
        </span>
      </TableCell>
      <TableCell>
        <AccountMark account={s.account} product={s.product} email={s.account?.email} slot={subSlotText(s)} />
      </TableCell>
      <TableCell>{moneyBoth(s.price, s.priceFx)}</TableCell>
      <TableCell>
        <DueBadge endDate={s.endDate} status={s.status} />
      </TableCell>
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <ServiceRowActions
          sub={s}
          customer={extra?.customer || customerByContact.get(Number(s.contactId || s.group?.contactId))}
          payments={payments.records || []}
          onEdit={() => openCart(s.group?.contactId || s.contactId)}
          onRenew={() => openCart(s.group?.contactId || s.contactId)}
          onNotify={notifyCustomer}
          onDeleted={refresh}
          onChanged={refresh}
        />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <PageFrame
        lockBody
        title={tNegocio("pages.services.title")}
        guide={{ id: "sales", to: "/negocio/servicios", startId: "cart" }}
        subtitle={tNegocio("pages.services.subtitle")}
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setConfigPreset(null);
              setConfigOpen(true);
            }}
          >
            {tNegocio("pages.services.cart.new")}
          </Button>
        }
      >
        <div className={classes.kpiGrid}>
          <KpiTile icon={PeopleIcon} label={tNegocio("pages.services.kpis.clients")} value={loading ? "…" : kpis.clients} onClick={() => setKind("")} />
          <KpiTile icon={CheckCircleOutlineIcon} tone="kpiToneGood" label={tNegocio("pages.services.kpis.live")} value={loading ? "…" : kpis.live} onClick={() => setKind("")} />
          <KpiTile icon={EventBusyIcon} tone="kpiToneBad" label={tNegocio("pages.services.kpis.expired")} value={loading ? "…" : kpis.expired} onClick={() => setKind("expired")} />
          <KpiTile icon={HourglassEmptyIcon} tone="kpiToneWarn" label={tNegocio("pages.services.kpis.pending")} value={loading ? "…" : kpis.pending} onClick={() => setKind("pending")} />
        </div>

        <div className={classes.filterBar}>
            {isMobile && !user?.super ? null : (
              <>
            <FilterChips
              value={view}
              onChange={setView}
              options={[
                { value: "cart", label: tNegocio("pages.services.view.cart") },
                ...(isMobile ? [] : [{ value: "list", label: tNegocio("pages.services.view.list") }]),
                ...(user?.super ? [{ value: "watoolx", label: `${tNegocio("pages.services.view.watoolx")}${watoolx.loading ? "" : ` (${watoolx.rows.length})`}` }] : []),
              ]}
            />
            <span className={classes.filterSplit} />
              </>
            )}
            {view === "watoolx" ? null : (
              <>
            <FilterChips
              value={party}
              onChange={setParty}
              options={[
                { value: "", label: `${tNegocio("filters.all")} (${groups.length})` },
                { value: "final", label: `${tNegocio("pages.customers.types.final")} (${finalCount})` },
                { value: "reseller", label: `${tNegocio("pages.customers.types.reseller")} (${resellerCount})` },
              ]}
            />
            <span className={classes.filterSplit} />
            <FilterChips
              value={kind}
              onChange={setKind}
              options={[
                { value: "", label: tNegocio("filters.all") },
                { value: "active", label: tNegocio("status.active") },
                { value: "soon", label: tNegocio("pages.subscriptions.soonShort") },
                { value: "pending", label: tNegocio("status.pending_payment") },
                { value: "expired", label: tNegocio("status.expired") },
              ]}
            />
              </>
            )}
        </div>
        <SearchField fullWidth value={query} onChange={setQuery} placeholder={tNegocio("pages.services.search")} />

        {view === "watoolx" && user?.super ? (
          <WatoolxServicesView data={watoolx} query={query} />
        ) : rows.length ? (
          view === "list" && !isMobile ? (
            <div className={`${classes.tableWrap} ${classes.tableWrapFill}`}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{tNegocio("cols.contact")}</TableCell>
                    <TableCell>{tNegocio("cols.product")}</TableCell>
                    <TableCell>{tNegocio("cols.account")}</TableCell>
                    <TableCell>{tNegocio("cols.price")}</TableCell>
                    <TableCell>{tNegocio("cols.status")}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listPager.slice.map((s) =>
                    renderServiceRow(s, {
                      contactCell: (
                        <>
                          <span style={{ fontWeight: 700 }}>{s.group.name}</span>
                          {s.group.number ? <div className={classes.captionBlock}>{s.group.number}</div> : null}
                        </>
                      ),
                      onRowClick: () => openCart(s.group.contactId),
                    })
                  )}
                </TableBody>
              </Table>
              <NegocioPager
                count={listPager.count}
                page={listPager.page}
                rowsPerPage={listPager.rowsPerPage}
                onChangePage={listPager.setPage}
                onChangeRowsPerPage={listPager.setRowsPerPage}
              />
            </div>
          ) : (
            <div className={`${classes.tableWrap} ${classes.tableWrapFill}`}>
              {groupPager.slice.map((g, index) => {
                const customer = customerByContact.get(g.contactId);
                const amount = (
                  <div className={classes.collectAmount}>
                    <div style={{ fontWeight: 800 }}>{money(g.amount)}</div>
                    <div className={classes.captionBlock}>{g.items.length}</div>
                  </div>
                );
                const actions = customer ? (
                  <div className={classes.serviceCardActions} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Datos del cliente">
                      <IconButton size="small" aria-label="Datos del cliente" onClick={() => history.push(`/negocio/clientes?contactId=${g.contactId}`)}>
                        <PersonOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <WalletBalanceAction customer={customer} />
                    <CartRowActions
                      cart={g}
                      customer={customer}
                      now={now}
                      onChanged={async () => {
                        await refresh();
                        if (Number(cartId) === Number(g.contactId)) openCart(null);
                      }}
                      onNotifyCart={notifyCart}
                      onRenewed={(payload) => setSendPrompt(payload)}
                    />
                  </div>
                ) : null;
                return (
                  <div
                    key={g.contactId}
                    className={`${classes.clickableRow} ${classes.serviceCustomerCard}`}
                    style={{ animationDelay: `${Math.min(index, 5) * 35}ms` }}
                    onClick={() => openCart(g.contactId)}
                    onKeyDown={(event) => {
                      if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); openCart(g.contactId); }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className={classes.collectRow}>
                      <div className={classes.collectIdentity}>
                        <ContactAvatar name={g.name} number={g.number} profilePicUrl={customer?.profilePicUrl} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 800 }}>{g.name}</div>
                          <div className={classes.captionBlock}>
                            {g.code}
                            {g.number ? ` · ${g.number}` : ""}
                          </div>
                        </div>
                      </div>
                      {isMobile ? (
                        <>
                          <div className={classes.collectMetaRow}>{amount}</div>
                          {actions}
                        </>
                      ) : (
                        <>
                          {amount}
                          {actions}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              <NegocioPager
                count={groupPager.count}
                page={groupPager.page}
                rowsPerPage={groupPager.rowsPerPage}
                onChangePage={groupPager.setPage}
                onChangeRowsPerPage={groupPager.setRowsPerPage}
              />
            </div>
          )
        ) : loading ? (
          <LoadingHint />
        ) : (
          <EmptyHint title={tNegocio("pages.services.emptyTitle")} subtitle={tNegocio("pages.services.empty")} />
        )}
      </PageFrame>
      <CartConfigDialog
        open={configOpen}
        customers={customers.records || []}
        payments={payments.records || []}
        preset={configPreset}
        onClose={() => setConfigOpen(false)}
        onSaved={async (contactId) => {
          setConfigOpen(false);
          await customers.reload();
          openCart(contactId);
        }}
      />
      <CartDetailDialog
        open={!!cartId && !configOpen && !addOpen}
        cart={cartGroup}
        customer={cartCustomer}
        payments={payments.records || []}
        now={now}
        onClose={() => openCart(null)}
        onEditConfig={() => {
          setConfigPreset(cartCustomer);
          setConfigOpen(true);
        }}
        onAddItem={() => setAddOpen(true)}
        onChanged={refresh}
        onNotify={notifyCustomer}
        onNotifyCart={notifyCart}
        onPeekAccount={showAccount}
      />
      <AddCartItemDialog
        open={addOpen}
        customer={cartCustomer}
        payments={payments.records || []}
        cartHasItems={!!(cartGroup?.items || []).length || batchSinceRef.current != null}
        onClose={() => setAddOpen(false)}
        onSaved={(result) => {
          const customer = cartCustomer;
          setAddOpen(false);
          setNow(Date.now());
          openSendLast(customer, result?.subscriptionIds || []);
          refresh();
        }}
      />
      <AccountPeek
        open={!!peekAccountId}
        accountId={peekAccountId}
        onClose={() => showAccount(null)}
        onChanged={refresh}
      />
      <SendPromptDialog
        prompt={sendPrompt}
        onClose={closeSendPrompt}
        onAddAnother={(prompt) => {
          setSendPrompt(null);
          if (prompt?.customer) {
            openCart(prompt.customer.contactId);
            setAddOpen(true);
          }
        }}
        onSend={async (prompt, channel) => {
          if (prompt.cart || ["order", "last", "reminder", "update", "renewed"].includes(prompt.kind)) {
            await notifyCart({
              kind: prompt.kind || "last",
              channel,
              contactId: prompt.customer?.contactId,
              subscriptionIds: prompt.subscriptionIds,
            });
          } else {
            await notifyCustomer({
              kind: prompt.kind || "renew",
              channel,
              contactId: prompt.customer?.contactId,
              subscriptionId: prompt.subscriptionId,
            });
          }
          closeSendPrompt();
        }}
      />
    </>
  );
}
